import React, { type ReactNode, useMemo } from "react";
import { Avatar } from "components/src/components/Avatar/Avatar";
import type { SemanticColorType } from "shared/src/styles/colorStyles";
import type { IconStringReturnType } from "components/src/utils/parseIconString";
import type { AvatarProps } from "components/src/components/Avatar/types";
import type { ThemeModeType } from "shared/src/types";
import { TeamLogo } from "components-sbk/src/components/TeamLogo/TeamLogo";
import type { TeamInfoReturnType } from "shared/src/hooks/getTeamInfo";
import type { CompetitionInfoReturnType } from "shared/src/hooks/getCompetitionInfo";
import { Background } from "components/src/components/Background/Background";
import { TeamMatchup } from "../../TeamMatchup/TeamMatchup";

const AvatarWrapper = ({ children }: { children: ReactNode }) => (
  <div tabIndex={-1} role="presentation">
    {children}
  </div>
);

export const QuickPickAvatar = ({
  image,
  showAvatar,
  promoName = "",
  hasFoundTeam,
  hasFoundTeams,
  foundTeam,
  foundTeams,
  foundLeague, 
  hasFoundLeague,
  isLive,
  mode,
  iconColor = "fg-global-white",
  useAtForTeamMatchup = false 
}: {
  image?: IconStringReturnType;
  showAvatar?: boolean;
  promoName?: string | null;
  team?: string;
  teams?: string[];
  foundTeam?: TeamInfoReturnType;
  foundTeams?: TeamInfoReturnType[];
  foundLeague?: CompetitionInfoReturnType;
  hasFoundTeam?: boolean;
  hasFoundTeams?: boolean;
  hasFoundLeague?: boolean;
  isLive?: boolean;
  mode: ThemeModeType;
  iconColor?: string;
  useAtForTeamMatchup?: boolean;
}) => {
  if (!showAvatar) return null;

  // Default avatarProps - QuickPick icon.
  let avatarProps: AvatarProps = {
    icon: "icon_ui_quick-pick",
    iconColor,
    size: "4xl",
  };

  const hasImage = typeof image === "object" && !!image;
  
  // Alt Text
  let altText = "";
  if (hasImage) {
    altText = `Icon image ${image?.name}`;
    switch (true) {
      case image?.isUrl:
        altText = `Promotional image for the sponsor ${promoName}`;
        break;
      case image?.isTeam:
        altText = `Logo for the team ${image?.name}`;
        break;
    }
  } else if (hasFoundTeam) {
    altText = `Logo for the team ${foundTeam?.name}`;
  } else {
    altText = `Image of ${image ?? foundTeam?.name ?? ""}`;
  }

  if (hasFoundTeam && foundTeam) {
    if (foundTeam.league === "countries") {
      avatarProps = {
        icon: foundTeam.metadata.images?.primary?.name,
        iconColor,
        size: "4xl",
      };
    } else {
      avatarProps = {
        icon: (
          <TeamLogo
            name={foundTeam.name}
            accessibilityLabel={altText}
            size="4xl"
            sport={typeof image === "object" ? image?.league : foundTeam.league}
          />
        ),
      };
    }
  }
  if (image?.isUrl) avatarProps = { src: image.name };
  if (image?.isIcon) avatarProps = { icon: image.name, iconColor };

  const bgColors = isLive ? {
    primaryColor: "var(--highlight-accent)",
    secondaryColor: "var(--fg-global-black)"
  } 
  : hasFoundLeague ? {
    primaryColor: foundLeague?.metadata.colors?.primary.value,
    secondaryColor: foundLeague?.metadata.colors?.secondary.value
    } 
  : hasFoundTeam ? {
    primaryColor: foundTeam?.metadata.colors?.primary.value,
    secondaryColor: foundTeam?.metadata.colors?.secondary.value
  } : {};

  const _bgElement = useMemo(() => {
    return (
      <Background 
        colorType={hasFoundTeam && !isLive ? "dark" : "normal"}
        gradient="vertical"
        colorMode="both"
        position="absolute"
        {...bgColors}
        fallbackPrimaryColor="var(--fg-primary)"
        fallbackSecondaryColor="var(--silver-1100)"
      />
    );
  }, []);

  const _computedAvatar = useMemo(() => {
    if (foundTeams && hasFoundTeams) {
      return (
        <TeamMatchup firstTeam={foundTeams.at(0)} secondTeam={foundTeams.at(1)} isLive={isLive} useAt={useAtForTeamMatchup} />
      );
    } else {
      return (
        <Avatar
          {...avatarProps}
          wrapperClasses="cui-shadow-md cui-rounded-[9999px]"
          size="3xl"
          bgColor={"bg-default" as SemanticColorType}
          bgElement={_bgElement}
          borderColor={
            isLive ? "highlight-accent" : "fg-minimal"
          }
          altText={altText}
          tabIndex={-1}
          mode="dark"
        />
      );
    }
  }, []);

  return <AvatarWrapper>{_computedAvatar}</AvatarWrapper>;
};

